import type { AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import { apiClient } from './api-client';
import type { Envelope } from './interceptors/response.interceptor';

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface RequestOptions {
  query?: QueryParams;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  timeoutMs?: number;
}

// Empty filters ("", null, undefined) are left out of the query string.
function withoutEmpty(query: QueryParams = {}): QueryParams {
  return Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );
}

// What the services call, shaped like Angular's HttpClient. Paths are relative to /api/v1 and
// results are unwrapped from the API envelope.
export class HttpClient {
  constructor(
    private readonly client: AxiosInstance,
    private readonly options: { tenant: boolean },
  ) {}

  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, opts);
  }

  async getPage<T>(path: string, opts?: RequestOptions): Promise<Page<T>> {
    const envelope = await this.send('GET', path, undefined, opts);
    return { items: envelope.data as T[], nextCursor: envelope.meta?.nextCursor ?? null };
  }

  // Numbered pages (page, total, totalPages) for lists that jump to a page instead of scrolling.
  async getList<T, M>(path: string, opts?: RequestOptions): Promise<{ items: T[]; meta: M }> {
    const envelope = await this.send('GET', path, undefined, opts);
    return { items: envelope.data as T[], meta: envelope.meta as M };
  }

  post<T = unknown>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, body, opts);
  }

  put<T = unknown>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, body, opts);
  }

  patch<T = unknown>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, opts);
  }

  delete<T = unknown>(path: string, opts?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, opts);
  }

  private async request<T>(
    method: Method,
    path: string,
    body: unknown,
    opts?: RequestOptions,
  ): Promise<T> {
    const envelope = await this.send(method, path, body, opts);
    return envelope.data as T;
  }

  private async send(
    method: Method,
    path: string,
    body: unknown,
    opts: RequestOptions = {},
  ): Promise<Envelope> {
    const config: AxiosRequestConfig = {
      method,
      url: path,
      data: body,
      params: withoutEmpty(opts.query),
      headers: opts.headers,
      signal: opts.signal,
      timeout: opts.timeoutMs,
      tenant: this.options.tenant,
    };
    const response = await this.client.request<Envelope>(config);
    return response.data;
  }
}

// Account-level endpoints (/auth, /business/me, /admin).
export const http = new HttpClient(apiClient, { tenant: false });
// Endpoints for the selected business: every request carries X-Business-Id.
export const tenantHttp = new HttpClient(apiClient, { tenant: true });
